import {
  Center,
  Flex,
  VStack,
  Divider,
  Text,
  Stack,
  StackDivider,
  Box,
  Icon
} from '@chakra-ui/react';
import { useEffect } from 'react';
import { useNavigate, useSearchParams, Link as ReactRouterLink } from 'react-router-dom';

import ReskinCardHeader from '@components/general/card/reskin-card-structure/reskin-card-header.component';
import { StatusTimeLabel } from '@components/general/card/status-time-label.component';
import MeetingParticipants from '@components/meeting/meeting-participants.component';
import TermsAndConditions from '@components/login/terms-conditions.component';
import { useBreakpointValue, useLoginShared, useSetDocumentTitle } from '@util/hooks';
import OAuthStack from './oauth-stack.component';
import { useUserStore } from '@store/app.store';
import { getLensIconComponent, normalizeTimestamp } from '@util/helpers';

export default function GuestLoginPage() {
  useSetDocumentTitle('Login - FORA');
  const isAuthenticated = useUserStore((state) => state.isAuthenticated());
  const navigate = useNavigate();
  const { openCredsErrorToast, openErrorToast } = useLoginShared();
  const [params] = useSearchParams();

  const guestEmail = params.get('email');
  const senderName = params.get('senderName');

  // share-meeting-details params
  const sharedMeetingId = params.get('meetingId');
  const meetingTitle = params.get('meetingTitle');
  const startTime = Number(params.get('startTime'));
  const endTime = Number(params.get('endTime'));
  const participantNames = params.get('participantNames');

  // share-insight-details params
  const insightId = params.get('insightId');
  const insightHeadline = params.get('insightHeadline');
  const insightSubheadline = params.get('insightSubheadline');
  const initiativeTitle = params.get('initiativeTitle');

  let redirectUrl;
  if (insightId) {
    redirectUrl = `/insights/${insightId}`;
  } else if (sharedMeetingId) {
    redirectUrl = `/meeting/${sharedMeetingId}`;
  } else {
    redirectUrl = '/';
  }

  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectUrl, { replace: true });
    }
  }, [isAuthenticated, redirectUrl]);

  return (
    <Flex
      data-cy='guest-login-page'
      h='full'
      bgColor='surface.containerLow'
      alignItems='flex-start'
      flexDir='column'
    >
      <Center
        h='full'
        w='full'
        px={{ base: 4, lg: 0 }}
      >
        <VStack
          gap={8}
          maxW='720px'
        >
          <Text
            fontSize='lg'
            color='surface.onSurface'
            fontWeight={600}
          >
            {senderName} shared {insightId ? 'an insight' : 'a meeting summary'} with you:
          </Text>
          {insightId ? (
            <SharedInsight
              link={window.location.href}
              insightId={insightId}
              insightHeadline={insightHeadline}
              insightSubheadline={insightSubheadline}
              initiativeTitle={initiativeTitle}
            />
          ) : (
            <SharedMeeting
              link={window.location.href}
              meetingTitle={meetingTitle}
              startTime={startTime}
              endTime={endTime}
              participantNames={participantNames}
            />
          )}
          <Text
            fontSize='lg'
            color='surface.onSurface'
            fontWeight={300}
          >
            Connect your account ({guestEmail}) to view the summary:
          </Text>
          <Flex
            w='full'
            borderRadius='xl'
            bgColor='surface.containerLowest'
            p={4}
            border='reskin'
          >
            <OAuthStack
              openErrorToast={openErrorToast}
              openCredsErrorToast={openCredsErrorToast}
              redirectUrl={redirectUrl}
              isGuest
            />
          </Flex>
          <Divider />
          <TermsAndConditions />
        </VStack>
      </Center>
    </Flex>
  );
}

const SharedInsight = ({ link, insightHeadline, insightSubheadline, initiativeTitle }) => {
  const headerSizeVariant = useBreakpointValue({ base: 'md', md: 'lg' });

  return (
    <Stack
      w='full'
      layerStyle='section-card'
      data-testid='shared-insight'
      gap={0}
    >
      <ReskinCardHeader
        title={insightHeadline}
        titleLink={link}
        size={headerSizeVariant}
        leftElement={
          <Icon
            as={getLensIconComponent()}
            color='brandPrimary.onContainer'
            boxSize={6}
            my={1.5}
            mr={2}
          />
        }
      >
        <Flex
          flexDir={{ base: 'column', md: 'row' }}
          gap={{ base: '7px', md: 'unset' }}
        >
          <Flex ml={{ base: 0, md: 'auto' }}>
            <Text
              as='span'
              color='surface.onSurfaceVariant'
              variant='label-lighter-md'
              lineHeight='18px'
              mr={1.5}
            >
              In
            </Text>
            <Text
              as='span'
              color='brandPrimary.primary'
              variant='label-caps-600-md'
              letterSpacing='1.5px'
              layerStyle='ellipsis-text'
            >
              {initiativeTitle}
            </Text>
          </Flex>
        </Flex>
      </ReskinCardHeader>
      <Divider style={{ margin: 0 }} />
      <Box
        as={ReactRouterLink}
        to={link}
        px={{ base: 4, md: 8 }}
        pt={3}
        pb={1.5}
      >
        <Text
          variant='body-lighter-md'
          as='span'
        >
          {insightSubheadline}
        </Text>
      </Box>
    </Stack>
  );
};

const SharedMeeting = ({ link, meetingTitle, startTime, endTime, participantNames }) => {
  const participants = participantNames?.split(',').map((p) => {
    return { name: p, email: p };
  });

  const meeting = {
    title: meetingTitle,
    start_at: normalizeTimestamp(startTime),
    end_at: normalizeTimestamp(endTime),
    nylas: {
      organizer: participants?.[0],
      participants
    }
  };

  return (
    <Stack
      data-testid='shared-meeting'
      divider={<StackDivider style={{ margin: 0 }} />}
      layerStyle='section-card'
      w='full'
      h='fit-content'
    >
      <ReskinCardHeader
        title={meeting.title}
        titleLink={link}
      >
        <StatusTimeLabel
          meeting={meeting}
          bot={{}}
          includeDate
          showAgoText
          px={0}
        />
      </ReskinCardHeader>
      <Flex p={3}>
        <MeetingParticipants
          meeting={meeting}
          showParticipantStatus={false}
        />
      </Flex>
    </Stack>
  );
};
